import { useMemo } from "react"
import { Shield } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import { escalationReasonIcon } from "./escalation-reason-icon"
import type { EscalationReason } from "@/types/agent_escalation_reason"

// Grid template shared by the column header and every row, modeled on
// /topics' TopicList:
//   28px icon | 1.6fr name+key+desc | 88px used | 92px last used | 1fr provenance
// Desktop only — on mobile the row stacks: icon+name on top, then a
// stats strip (used · last used · provenance) below.
export const CATALOG_GRID_COLS =
  "md:grid md:grid-cols-[28px_minmax(0,1.6fr)_88px_92px_minmax(0,1fr)] md:items-center md:gap-3"

export function EscalationReasonCatalogRow({
  reason,
  selected,
  onClick,
}: {
  reason: EscalationReason
  selected: boolean
  onClick: () => void
}) {
  const Icon = escalationReasonIcon(reason.icon)
  const isCatchAll = reason.key === "unclassified"

  const provenanceText = useMemo(() => {
    if (reason.status === "archived") return `archived ${fmtCompactRelative(reason.updatedAt)}`
    if (reason.status === "pending")  return `classifier · ${fmtCompactRelative(reason.createdAt)}`
    if (reason.provenance === "human" && reason.createdByName) {
      return `${reason.createdByName} · ${fmtCompactRelative(reason.createdAt)}`
    }
    return `classifier · ${fmtCompactRelative(reason.createdAt)}`
  }, [reason.status, reason.provenance, reason.createdByName, reason.createdAt, reason.updatedAt])

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick() } }}
      className={cn(
        CATALOG_GRID_COLS,
        "cursor-pointer border border-transparent border-b border-b-border/60 px-4 py-3 outline-none transition last:border-b-0",
        selected
          ? "rounded-control border-brand-red bg-brand-redSoft/20 shadow-[inset_0_0_0_1px_rgb(220_38_38)]"
          : "hover:bg-white/[0.02] focus-visible:bg-white/[0.04]",
      )}
    >
      {/* Mobile: icon + name+key+desc share a flex row at the top. */}
      <div className="flex items-start gap-3 md:contents">
        <div className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-brand-redSoft",
          selected ? "border-brand-red text-brand-red" : "border-brand-red/30 text-brand-red",
        )}>
          <Icon size={12} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-medium text-app-text">{reason.name}</div>
            {isCatchAll && (
              <span
                title="This reason is the synchronous default for newly-escalated convos and cannot be archived."
                className="inline-flex shrink-0 items-center gap-1 rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted"
              >
                <Shield size={9} strokeWidth={2.5} />
                Protected
              </span>
            )}
          </div>
          <div className={cn(
            "truncate font-mono text-[11px]",
            selected ? "text-brand-red" : "text-app-muted",
          )}>{reason.key}</div>
          {reason.description && (
            <div className="mt-0.5 truncate text-xs text-app-muted">{reason.description}</div>
          )}
        </div>
      </div>
      {/* Stats strip — flex-wrap on mobile, original 3 grid cells on desktop. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-muted md:contents">
        <div className="md:text-sm md:tabular-nums md:text-app-text">
          <span className="md:hidden">Used: </span>
          <span className="tabular-nums">{reason.timesUsed}</span>
        </div>
        <div>
          <span className="md:hidden">Last used: </span>
          {reason.lastUsedAt ? fmtCompactRelative(reason.lastUsedAt) : "never"}
        </div>
        <div className="truncate" title={provenanceText}>
          {provenanceText}
        </div>
      </div>
    </div>
  )
}
